import { useState, FormEvent } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { MarkdownEditor } from './MarkdownEditor';
import type { Activity, Car, SingleResponse } from '../api/types';

const ACTIVITY_TYPE_KEYS = [
  'repair',
  'milestone',
  'need_a_hand',
  'garage_sale',
  'trade_offer',
  'hosting_event',
  'car_show',
];

interface Props {
  open: boolean;
  car: Car;
  onClose: () => void;
  onCreated: () => void;
}

export function NewActivityModal({ open, car, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const [type, setType] = useState('repair');
  const [summary, setSummary] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [locationName, setLocationName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await apiFetch<SingleResponse<Activity>>('/api/v1/activities', {
        method: 'POST',
        body: JSON.stringify({
          type,
          car_id: car.id,
          summary,
          scheduled_at: scheduledAt || null,
          location_name: locationName || null,
          is_public: true,
        }),
      });
      onCreated();
    } catch (err: any) {
      setError(err.message ?? t('newActivity.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('newActivity.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <FormControl fullWidth>
              <InputLabel id="activity-type-label">{t('newActivity.activityType')}</InputLabel>
              <Select
                labelId="activity-type-label"
                label={t('newActivity.activityType')}
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {ACTIVITY_TYPE_KEYS.map((key) => (
                  <MenuItem key={key} value={key}>
                    {t(`activityType.${key}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <MarkdownEditor
              label={t('newActivity.summary')}
              value={summary}
              onChange={setSummary}
              rows={6}
              required
            />

            <TextField
              label={t('newActivity.time')}
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label={t('newActivity.place')}
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? t('newActivity.submitting') : t('common.submit')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
